import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { get, post, SESSION_EXPIRED_EVENT } from '@/api/client'
import { hashPassword } from '@/lib/passwordHash'

export interface AuthUser {
  id: string
  email: string
  fullName: string
  initials: string
  department: string | null
  role: string
}

export interface AuthOrg {
  id: string
  name: string
  slug: string
  domain: string | null
  onboardingCompletedAt: string | null
}

export interface Me {
  user: AuthUser
  organization: AuthOrg
  onboardingCompleted: boolean
  hasProfile: boolean
  /** Permission keys from app/core/permissions.py. Use `can()` from lib/permissions. */
  permissions: string[]
  customRole: { id: string; name: string } | null
  mfaEnabled?: boolean
  /** A temporary password that must be changed before the app can be used. */
  mustChangePassword?: boolean
}

/** A correct password with two-factor on: no session yet, only a short-lived challenge. */
export interface MfaChallenge {
  mfaRequired: true
  challenge: string
  expiresIn: number
}

export function isMfaChallenge(value: unknown): value is MfaChallenge {
  return !!value && typeof value === 'object' && (value as { mfaRequired?: unknown }).mfaRequired === true
}

interface AuthContextValue {
  me: Me | null
  loading: boolean
  login: (email: string, password: string) => Promise<Me | MfaChallenge>
  verifyMfa: (input: { challenge: string; code?: string; recoveryCode?: string }) => Promise<Me>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  setMe: (me: Me | null) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)

  const bootstrap = useCallback(async () => {
    try {
      // An expired access cookie is renewed inside the API client, so a 401 that
      // still reaches here means there is no session to restore.
      setMe(await get<Me>('/auth/me'))
    } catch {
      setMe(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void bootstrap()
  }, [bootstrap])

  // The API client could not renew the session (refresh token expired or
  // revoked): drop the user so the route guards send them to sign-in, instead
  // of leaving a page full of 401s.
  useEffect(() => {
    const expired = () => setMe(null)
    window.addEventListener(SESSION_EXPIRED_EVENT, expired)
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, expired)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      me,
      loading,
      setMe,
      login: async (email, password) => {
        const next = await post<Me | MfaChallenge>('/auth/login', {
          email,
          password: await hashPassword(password, email),
        })
        if (!isMfaChallenge(next)) setMe(next)
        return next
      },
      verifyMfa: async (input) => {
        const next = await post<Me>('/auth/mfa', input)
        setMe(next)
        return next
      },
      logout: async () => {
        try {
          await post('/auth/logout')
        } finally {
          setMe(null)
        }
      },
      refresh: async () => {
        setMe(await post<Me>('/auth/refresh'))
      },
    }),
    [me, loading],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
